import "./site/error-beacon";
import { Phalanx, PhalanxResult, RUN_MS } from "./games/phalanx/phalanx";
import { recordRun, showXpToast } from "./site/profile";
import { enqueueFailedSubmit, flushFailedSubmits, isRetryable } from "./site/offline-queue";
import "./ui/styles.css";

const PB_KEY = "aether.phalanx_pb";
const HANDLE_KEY = "aether.handle";

flushFailedSubmits("phalanx");

interface StartResp { run_id: string; seed: number; token: string; game: string }

async function startPhalanxRun(): Promise<StartResp | null> {
  try {
    const res = await fetch("/api/runs/phalanx", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) return null;
    return (await res.json()) as StartResp;
  } catch { return null; }
}

async function finishPhalanxRun(runId: string, handle: string, inputs: PhalanxResult["inputs"]) {
  const url = `/api/runs/phalanx/${runId}`;
  const body = { handle, inputs };
  try {
    const res = await fetch(url, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      if (isRetryable(res.status)) enqueueFailedSubmit("phalanx", url, body);
      return null;
    }
    return await res.json();
  } catch {
    enqueueFailedSubmit("phalanx", url, body);
    return null;
  }
}

const canvas = document.getElementById("stage") as HTMLCanvasElement;
const hud = document.querySelector(".hud") as HTMLElement;
const hudKills = document.getElementById("hud-kills")!;
const hudTime = document.getElementById("hud-time")!;
const hudAlive = document.getElementById("hud-alive")!;
const hudScore = document.getElementById("hud-score")!;
const msgTitle = document.getElementById("msg-title")!;
const msgBody = document.getElementById("msg-body")!;
const btnStart = document.getElementById("btn-start") as HTMLButtonElement;
const submitForm = document.getElementById("submit-form") as HTMLFormElement;
const handleInputOverlay = document.getElementById("handle-input-overlay") as HTMLInputElement;

let game: Phalanx | null = null;
let activeRunId: string | null = null;
let pendingResult: PhalanxResult | null = null;

async function startRun() {
  if (game) game.dispose();
  const runStart = await startPhalanxRun();
  activeRunId = runStart?.run_id ?? null;
  const seed = runStart?.seed ?? Math.floor(Math.random() * 0x7fffffff);

  game = new Phalanx(canvas, {
    onHud: (runMs, kills, alive, scoreEstimate) => {
      hudKills.textContent = kills.toString();
      hudTime.textContent = ((RUN_MS - runMs) / 1000).toFixed(1);
      hudAlive.textContent = alive.toString();
      hudScore.textContent = scoreEstimate.toLocaleString();
    },
    onGameOver: async (result) => {
      hud.dataset.state = "over";
      msgTitle.textContent = result.died ? "Hull breach" : (result.waves_cleared ? "Formation cleared" : "Time");
      const saucerNote = result.saucer_hits > 0
        ? ` · ${result.saucer_hits} saucer${result.saucer_hits === 1 ? "" : "s"}` +
          (result.saucer_perfects > 0 ? ` (${result.saucer_perfects} perfect!)` : "")
        : "";
      const barrierNote = ` · ${result.barriers_remaining}/3 barriers held`;
      msgBody.textContent = `${result.scoreEstimate.toLocaleString()} pts · ${result.kills} kills${saucerNote}${barrierNote}`;
      btnStart.textContent = "Try again";
      try {
        const pb = parseInt(localStorage.getItem(PB_KEY) || "0", 10) || 0;
        if (result.scoreEstimate > pb) localStorage.setItem(PB_KEY, String(result.scoreEstimate));
      } catch { /* private */ }
      const xp = recordRun({ game: "phalanx", score: result.scoreEstimate });
      showXpToast(xp.earnedXp, xp.totalXp, xp.rank);
      if (!activeRunId) return;
      const handle = localStorage.getItem(HANDLE_KEY) ?? "";
      if (handle) {
        const res = await finishPhalanxRun(activeRunId, handle, result.inputs);
        if (res) msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
      } else {
        pendingResult = result;
        submitForm.hidden = false;
      }
    },
  }, seed);
  game.armAudio();
  try {
    const raw = localStorage.getItem("aether.settings");
    if (raw) {
      const s = JSON.parse(raw);
      if (typeof s.audioVolume === "number") game.setVolume(s.audioVolume);
      if (typeof s.muted === "boolean") game.setMuted(s.muted);
    }
  } catch { /* defaults */ }
  game.start();
  hud.dataset.state = "running";
  msgTitle.textContent = "Phalanx";
  msgBody.textContent = "";
}

btnStart.addEventListener("click", startRun);
submitForm.addEventListener("submit", async (e) => {
  e.preventDefault();
  if (!activeRunId || !pendingResult) return;
  const value = handleInputOverlay.value.trim();
  if (!/^[A-Za-z0-9_-]{1,16}$/.test(value)) return;
  localStorage.setItem(HANDLE_KEY, value);
  const res = await finishPhalanxRun(activeRunId, value, pendingResult.inputs);
  pendingResult = null;
  submitForm.hidden = true;
  if (res) msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
});
window.addEventListener("keydown", (e) => {
  if (e.target instanceof HTMLInputElement) return;
  if (e.code === "Enter" && hud.dataset.state !== "running") startRun();
});
